import { Link } from "react-router-dom";
import { Stamp, CalendarDays, Download, ExternalLink, FileText } from "lucide-react";
import { RevealSection } from "@/components/common/RevealSection";
import { courses, freeHindiResources } from "@/content/siteData";
import { Button } from "@/components/ui/button";

export default function CoursesPage() {
  return (
    <div className="px-6 md:px-12 lg:px-24" data-testid="courses-page">
      <RevealSection className="mx-auto w-full max-w-7xl py-14 md:py-20">
        <p className="text-base text-primary md:text-lg" data-testid="courses-label">
          Course pathways
        </p>
        <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl" data-testid="courses-heading">
          Choose your Hindi programme for life in India.
        </h1>
        <p className="mt-4 max-w-3xl text-sm text-muted-foreground md:text-base" data-testid="courses-copy">
          Programmes are designed for diplomats, embassy families, expats, and foreign nationals. Group and custom
          embassy/corporate packages are also available on request.
        </p>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base" data-testid="courses-duration-note">
          Each class is 1 hour and includes worksheets + free supporting materials.
        </p>

        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {courses.map((course) => (
            <article
              key={course.name}
              className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-white p-8 soft-card"
              data-testid={`course-card-${course.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="absolute left-0 top-0 h-2 w-full bg-accent" />
              <div className="flex-1">
                <Stamp className="mb-4 h-5 w-5 text-secondary" />
                <h2 className="text-3xl font-semibold text-primary">{course.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{course.subtitle}</p>
                <p className="mt-4 rounded-lg border border-border bg-muted/35 px-3 py-2 text-sm font-medium text-foreground" data-testid={`course-recommended-plan-${course.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  Recommended path: {course.recommendedPlan}
                </p>
                <p className="mt-3 flex items-center gap-2 text-sm text-foreground/90" data-testid={`course-lessons-${course.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CalendarDays className="h-4 w-4 text-primary" />
                  {course.lessons}
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground/90">
                  {course.features.map((feature) => (
                    <li key={feature} className="list-inside list-disc">
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                asChild
                className="mt-6 h-11 w-full rounded-full bg-primary text-primary-foreground transition-transform duration-300 hover:scale-[1.02]"
                data-testid={`course-select-${course.name.toLowerCase().replace(/\s+/g, "-")}-button`}
              >
                <Link to="/contact">Book free trial for this programme</Link>
              </Button>
            </article>
          ))}
        </div>

        <p className="mt-6 text-sm font-medium leading-relaxed text-foreground/90" data-testid="courses-custom-note">
          Need a custom plan for embassy teams or corporate groups? Contact Prabha for tailored batch structures,
          executive language support, and group learning pathways. Kids storytelling circles, small-group classes, and
          focused workshops are also available. All programmes can include as many sessions as needed and are tailored
          to each learner’s engagement level and progress.
        </p>

        <section
          id="free-hindi-resources"
          className="mt-8 rounded-2xl border border-border bg-white p-6 soft-card"
          data-testid="courses-free-hindi-resources-section"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-primary/80" data-testid="courses-free-hindi-resources-label">
            Free Hindi resources
          </p>
          <h3 className="mt-2 text-2xl font-semibold text-primary" data-testid="courses-free-hindi-resources-heading">
            Download free PDF resources
          </h3>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="courses-free-hindi-resources-copy">
            Click Download PDF. It will download directly to your device.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid="courses-free-hindi-resources-grid">
            {freeHindiResources.map((resource) => (
              <article
                key={resource.id}
                className="rounded-2xl border border-border bg-muted/20 p-4"
                data-testid={`courses-free-resource-card-${resource.id}`}
              >
                <p className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1 text-xs text-primary" data-testid={`courses-free-resource-audience-${resource.id}`}>
                  <FileText className="h-3.5 w-3.5" />
                  {resource.audience}
                </p>
                <h4 className="mt-3 text-lg font-semibold text-foreground" data-testid={`courses-free-resource-title-${resource.id}`}>
                  {resource.title}
                </h4>
                <p className="mt-1 text-sm text-muted-foreground" data-testid={`courses-free-resource-description-${resource.id}`}>
                  {resource.description}
                </p>

                <div className="mt-4">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-primary px-4 text-sm font-medium text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
                      data-testid={`courses-free-resource-open-${resource.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open PDF
                    </a>

                    <a
                      href={resource.url}
                      download={`${resource.id}.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-primary px-4 text-sm font-medium text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
                      data-testid={`courses-free-resource-download-${resource.id}`}
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </a>
                  </div>

                  <p className="mt-2 text-xs text-muted-foreground" data-testid={`courses-free-resource-help-${resource.id}`}>
                    If browser blocks preview, use Download PDF.
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

      </RevealSection>
    </div>
  );
}